import { useEffect, useMemo, useRef, useState } from 'react';

import { getView, type LiveEntity, type LiveTree } from '../../engine/prism.js';
import { keepKey, liveRows, type KeepItem, type KeepPart } from '../../lib/live-world.js';
import type { EditorCommand, EntityDesc } from '../../lib/scene-doc.js';
import { logEditor } from '../../state/console.js';
import { usePlay } from '../../state/play.js';
import { EmptyState } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { EntityInspector } from '../scene/Inspector.js';
import { InspectorProvider, type InspectorTarget } from '../scene/inspector-context.js';

/**
 * Play mode live Hierarchy + Inspector: the running world's entities
 * (scene, prefab, script-spawned, additive), inspected and edited in the
 * running game. Edits are play-mode only; parts marked "Keep" (and the ones
 * edited here, suggested) are written back to the scene when play stops.
 */

const TREE_MS = 500;
const ENTITY_MS = 250;

export function LivePanel() {
  const state = usePlay((s) => s.state);
  const selected = usePlay((s) => s.liveSelected);
  const keep = usePlay((s) => s.keep);
  const [tree, setTree] = useState<LiveTree | null>(null);
  const [entity, setEntity] = useState<LiveEntity | null>(null);
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const inspectorRef = useRef<HTMLDivElement | null>(null);
  const running = state === 'playing' || state === 'paused';

  useEffect(() => {
    if (!running) {
      setTree(null);
      return;
    }
    let live = true;
    const tick = () => {
      const view = getView('play');
      if (!view) setTree(null);
      else void view.liveTree().then((tree) => live && setTree(tree));
    };
    tick();
    const t = setInterval(tick, TREE_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [running]);

  useEffect(() => {
    if (!running || !selected) {
      setEntity(null);
      return;
    }
    let live = true;
    const editing = () => inspectorRef.current?.contains(document.activeElement) && document.activeElement?.tagName === 'INPUT';
    const tick = () => {
      // Don't replace values under the cursor while a field is being edited.
      if (editing()) return;
      const view = getView('play');
      if (!view) setEntity(null);
      else void view.liveEntity(selected).then((entity) => live && !editing() && setEntity(entity));
    };
    tick();
    const t = setInterval(tick, ENTITY_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [running, selected]);

  const rows = useMemo(() => liveRows(tree, collapsed, filter), [tree, collapsed, filter]);
  const keptKeys = useMemo(() => new Set(keep.map(keepKey)), [keep]);

  const target = useMemo<InspectorTarget>(() => {
    const isKept = (id: string, part: KeepPart) => keptKeys.has(keepKey({ id, part })) || keptKeys.has(keepKey({ id, part: 'entity' }));
    const partOf = (path: string): KeepPart => (path.startsWith('transform') ? 'transform' : `component:${path.split('.')[1] ?? ''}`);
    return {
      apply: async (command: EditorCommand) => {
        const view = getView('play');
        if (!view) return;
        const r = await (() => {
          switch (command.cmd) {
            case 'setTransform':
              return view.liveEdit({ op: 'setTransform', id: command.id, transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1], ...command.transform } as LiveEntity['transform'] });
            case 'setComponent':
              return view.liveEdit({ op: 'setComponent', id: command.id, key: command.key, value: command.value });
            case 'removeComponent':
              return view.liveEdit({ op: 'removeComponent', id: command.id, key: command.key });
            case 'renameEntity':
              return view.liveEdit({ op: 'rename', id: command.id, name: command.name });
            case 'deleteEntity':
              return view.liveEdit({ op: 'destroy', id: command.id });
            default:
              return Promise.resolve({ ok: false, error: `${command.cmd} is not available while playing` });
          }
        })();
        if (!r.ok) {
          logEditor('warn', `Live edit: ${r.error ?? 'failed'}`);
          return;
        }
        const part: KeepPart | null =
          command.cmd === 'setTransform' ? 'transform' : command.cmd === 'setComponent' || command.cmd === 'removeComponent' ? `component:${command.key}` : command.cmd === 'renameEntity' ? 'entity' : null;
        if (part && 'id' in command) usePlay.getState().markEdited({ id: command.id, part });
        setEntity(await view.liveEntity(selected ?? ''));
      },
      mark: (id, path) => (isKept(id, partOf(path)) ? 'kept' : null),
      fieldMenu: (id, path) => {
        const part = partOf(path);
        const kept = isKept(id, part);
        return [{ id: 'keep', label: kept ? 'Don’t keep when play stops' : 'Keep when play stops', icon: 'check', run: () => usePlay.getState().toggleKeep({ id, part }) }];
      },
      componentMenu: (id, key) => {
        const kept = isKept(id, `component:${key}`);
        return [
          { id: 'sep-keep', label: '', separator: true },
          { id: 'keep-c', label: kept ? 'Don’t keep this component' : 'Keep this component when play stops', icon: 'check', run: () => usePlay.getState().toggleKeep({ id, part: `component:${key}` }) },
        ];
      },
    };
  }, [keptKeys, selected]);

  if (!running) {
    return <EmptyState icon="play" title="Not playing">Press Play to inspect the running game.</EmptyState>;
  }

  const desc: EntityDesc | null = entity ? { id: entity.id, name: entity.name, parent: entity.parent, transform: entity.transform, components: entity.components } : null;
  const wholeKept = selected ? keptKeys.has(keepKey({ id: selected, part: 'entity' })) : false;

  return (
    <div className="live-panel">
      <div className="live-tree">
        <div className="panel-tools">
          <div className="search-input">
            <Icon name="search" size={13} />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search running entities" aria-label="Search running entities" />
          </div>
          <span className="muted mono live-count" title="Running entities">
            {tree?.entities.length ?? 0}
          </span>
        </div>
        <ul className="tree-list" role="tree">
          {rows.map(({ entity: e, depth, hasChildren }) => (
            <li
              key={e.id}
              role="treeitem"
              aria-selected={selected === e.id}
              className={`tree-item${selected === e.id ? ' is-selected' : ''}${e.spawned ? ' is-spawned' : ''}${e.scene ? ' is-additive' : ''}`}
              style={{ paddingLeft: 6 + depth * 14 }}
              onClick={() => usePlay.getState().selectLive(e.id)}
              title={`${e.id}${e.scene ? ` (${e.scene})` : e.spawned ? ' (spawned)' : ''}`}
            >
              <button
                type="button"
                className={`tree-caret${hasChildren ? '' : ' is-leaf'}`}
                tabIndex={-1}
                onClick={(ev) => {
                  ev.stopPropagation();
                  setCollapsed((c) => {
                    const next = new Set(c);
                    if (next.has(e.id)) next.delete(e.id);
                    else next.add(e.id);
                    return next;
                  });
                }}
              >
                {hasChildren && <Icon name={collapsed.has(e.id) ? 'chevronRight' : 'chevronDown'} size={11} />}
              </button>
              <span className="tree-label">{e.name || e.id}</span>
              {keep.some((k) => k.id === e.id) && <Icon name="check" size={11} className="live-kept" />}
            </li>
          ))}
        </ul>
      </div>
      <div className="live-inspector" ref={inspectorRef}>
        {desc ? (
          <InspectorProvider target={target}>
            <div className="live-keep-bar">
              <button
                type="button"
                className={`tb-btn${wholeKept ? ' is-active' : ''}`}
                title="Write this entity (transform, name, components) to the scene when play stops"
                onClick={() => usePlay.getState().toggleKeep({ id: desc.id, part: 'entity' } satisfies KeepItem)}
              >
                <Icon name="check" size={12} /> {wholeKept ? 'Kept on stop' : 'Keep entity on stop'}
              </button>
            </div>
            <EntityInspector entity={desc} commandError={null} readOnlyId />
          </InspectorProvider>
        ) : (
          <EmptyState icon="cursor" title={selected ? 'Entity is gone' : 'Select a running entity'} />
        )}
      </div>
    </div>
  );
}

/** Edited / kept parts of this run, listed above the log (Unity keeps nothing by default). */
export function KeepList() {
  const keep = usePlay((s) => s.keep);
  const edited = usePlay((s) => s.edited);
  const suggestions = edited.filter((e) => !keep.some((k) => keepKey(k) === keepKey(e)));
  if (keep.length === 0 && suggestions.length === 0) return null;
  return (
    <div className="keep-list" role="status">
      <span className="muted">Play-mode changes:</span>
      {keep.map((k) => (
        <button key={keepKey(k)} type="button" className="chip is-on" title="Kept when play stops — click to drop" onClick={() => usePlay.getState().toggleKeep(k)}>
          <Icon name="check" size={11} /> {k.id} {k.part === 'entity' ? '' : `· ${k.part.replace('component:', '')}`}
        </button>
      ))}
      {suggestions.map((k) => (
        <button key={keepKey(k)} type="button" className="chip" title="Edited in the live Inspector — click to keep it when play stops" onClick={() => usePlay.getState().toggleKeep(k)}>
          {k.id} {k.part === 'entity' ? '' : `· ${k.part.replace('component:', '')}`}
        </button>
      ))}
    </div>
  );
}
